<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import departmentService from '@/services/department.service'
import DepartmentDrawer from '@/views/masters/department/drawer/DepartmentDrawer.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

const searchQuery = ref('')

definePage({
  meta: {
    name: 'master-department',
    subject: 'global.menu',
    action: 'master_department',
  },  
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()
const snackbar = useToast()

const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data){
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'Code',
    key: 'code',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: departmentsData,
  execute: fetchDepartments,
} =  useApi(createUrl('/masters/departments', {
  query: {
    paged: 1,
    q: searchQuery,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const departments = computed(() => departmentsData.value?.data)
const totalDepartments = computed(() => departmentsData.value?.meta?.total)

const updateDepartment = data => {
  if (data.id){
    departmentService.updateDepartment(data.id, data, ()=>
    {
      fetchDepartments()
      isDepartmentDrawerVisible.value = false
      resetErrors()
    }, onError)
  }
}

const saveDepartment = data => {
  departmentService.addDepartment(data, ()=>
  {
    fetchDepartments()
    isDepartmentDrawerVisible.value = false
    resetErrors()

  }, onError)
}

const resetErrors = () => {
  errors.value = {
    name: [],
    code: [],
    status: [],
    can_have_escalation: [],
  }
}

const isDepartmentDrawerVisible = ref(false)
const departmentDrawerData = ref()
const departmentDeleteData = ref()
const departmentDrawerType = ref('Add')

const showDepartment = (departmentData, type) => {
  errors.value = {
    name: [],
    code: [],
    status: [],
  }
  departmentDrawerData.value = departmentData
  departmentDrawerType.value = type  
  isDepartmentDrawerVisible.value = true
}

const isConfirmDialogVisible = ref(false)
const refConfirmDialog = ref()

const showConfirmDialog = departmentData => {
  departmentDeleteData.value = departmentData
  isConfirmDialogVisible.value = true
}

const deleteDepartment = async (confirm, data) => {
  if (data?.id && confirm) {
    await departmentService.deleteDepartment(
      data.id, 
      () => {
        refConfirmDialog.value.onSuccess()
        isConfirmDialogVisible.value = false
        fetchDepartments()
      },
      () => {
        refConfirmDialog.value.onError()
      },
    )
  }
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value){
    departmentDeleteData.value = {}
  }
})

// ===== Import Department Functionality =====
const refImportDepartmentForm = ref()
const importDepartmentFile = ref()
const importDepartmentErrors = ref()
const isImportDepartmentLoading = ref(false)
const isImportDepartmentModal = ref(false)

const submitImportDepartment = async () => {
  if (!importDepartmentFile.value) {
    importDepartmentErrors.value = {
      file: ["Please select a file to import."],
    }

    return
  }
  isImportDepartmentLoading.value = true
  importDepartmentErrors.value = null // Clear previous errors

  try {
    await departmentService.import(
      {
        file: importDepartmentFile.value,
      },
      () => {
        // onSuccess callback
        snackbar.success("Departments imported successfully.")
        isImportDepartmentModal.value = false
        fetchDepartments() // Refresh the department list
        importDepartmentFile.value = null // Reset file input
      },
      error => {
        // onError callback
        importDepartmentErrors.value = error.data?.errors || {
          message:
            error.data?.message || "File upload failed. Please try again.",
        }
        snackbar.error(error.data?.message || "File upload failed.")
      },
    )
  } catch (e) {
    console.error("Error during department import submission:", e)
    snackbar.error(
      "An unexpected error occurred while submitting the import request.",
    )
    if (!importDepartmentErrors.value)
      importDepartmentErrors.value = { message: "Import submission failed." }
  } finally {
    isImportDepartmentLoading.value = false
  }
}

const downloadSampleDepartmentFile = async () => {
  await downloadFile(
    "masters/departments/sample-import-file/export",
    "department-sample-excel.xlsx",
    () => {}, // success
    () => {}, // error
  )
}

watch(
  () => isImportDepartmentModal.value,
  value => {
    if (value === false) {
      importDepartmentFile.value = null
      importDepartmentErrors.value = null
    }
  },
)

// ===== End Import Department Functionality =====
</script>

<template>
  <section>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: 'All' },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 10rem;">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>

          <!-- 👉 Export button -->
          <!--
            <VBtn
            variant="tonal"
            color="secondary"
            prepend-icon="tabler-screen-share"
            >
            Export
            </VBtn> 
          -->

          <!-- 👉 Import Departments Button -->
          <VBtn
            v-if="$can('import', 'master.department')"
            prepend-icon="tabler-download"
            @click="isImportDepartmentModal = !isImportDepartmentModal"
          >
            Import Departments
          </VBtn>
       
          <VBtn
            v-if="$can('create', 'master.department')"
            prepend-icon="tabler-plus"
            @click="showDepartment(null,'Add')"
          >
            Add New Department
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="departments"
        :items-length="totalDepartments"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <div
            class="d-flex align-center cursor-pointer"
            :departmentData="item"
            @click="showDepartment(item,'View')"
          >
            <VAvatar
              size="34"
              :variant="!item.avatar ? 'tonal' : undefined"
              class="me-3"
            >
              <VImg
                v-if="item.avatar"
                :src="item.avatar"
              />
              <span v-else>{{ avatarText(item.name) }}</span>
            </VAvatar>
            <div class="d-flex flex-column">
              <h6 class="text-base">
                {{ item.name }}
              </h6>
            </div>
          </div>
        </template>
        <template #item.code="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.code }}</span>
        </template>

    
        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn 
            v-if="$can('delete', 'master.department')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn 
            v-if="$can('update', 'master.department')"
            @click="showDepartment(item,'Edit')"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>

        <!-- pagination -->
        <!--
          <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
          <p class="text-sm text-disabled mb-0">
          {{ paginationMeta({ page, itemsPerPage }, totalDepartments) }}
          </p>

          <VPagination
          v-model="page"
          :length="Math.ceil(totalDepartments / itemsPerPage)"
          :total-visible="$vuetify.display.xs ? 1 : Math.ceil(totalDepartments / itemsPerPage)"
          >
          <template #prev="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Previous
          </VBtn>
          </template>

          <template #next="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Next
          </VBtn>
          </template>
          </VPagination>
          </div>
          </template> 
        -->
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>  
    <DepartmentDrawer
      v-model:isDrawerOpen="isDepartmentDrawerVisible"
      :department-data="departmentDrawerData"
      :type="departmentDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="departmentDrawerType = $event"
      @update:department-data="updateDepartment"
      @save-department="saveDepartment"
    />
    <ConfirmDialog
      ref="refConfirmDialog"
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Department Deleted."
      confirm-title="Deleted!"
      cancel-title="Error!"
      cancel-msg="Something Went Wrong."
      :is-confirm-visible="false"
      :is-error-visible="false"
      :data="departmentDeleteData"
      @confirm="deleteDepartment"
    />

    <!-- Import Department Modal -->
    <VDialog
      v-model="isImportDepartmentModal"
      max-width="600"
      :persistent="isImportDepartmentLoading"
    >
      <DialogCloseBtn
        size="small"
        :disabled="isImportDepartmentLoading"
        @click="isImportDepartmentModal = false"
      />
      <VCard>
        <VCardText class="pt-2">
          <h6 class="text-lg font-weight-medium mt-5">
            Import Departments
          </h6>
        </VCardText>

        <VCardText>
          <VForm ref="refImportDepartmentForm">
            <VFileInput
              label="Upload File"
              prepend-icon="tabler-file-import"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              :disabled="isImportDepartmentLoading"
              :error-messages="importDepartmentErrors?.file"
              @change="
                (e) => {
                  importDepartmentFile = e.target.files[0];
                }
              "
            />
          </VForm>
        </VCardText>

        <!-- General Errors -->
        <VCardText
          v-if="
            importDepartmentErrors?.message &&
              !Array.isArray(importDepartmentErrors)
          "
        >
          <VAlert
            color="error"
            variant="tonal"
            closable
            @click:close="importDepartmentErrors.message = null"
          >
            {{ importDepartmentErrors.message }}
          </VAlert>
        </VCardText>

        <!-- Validation Errors (row-specific from backend) -->
        <VCardText
          v-if="
            Array.isArray(importDepartmentErrors) &&
              importDepartmentErrors.length > 0
          "
        >
          <div
            class="d-flex flex-column gap-2"
            style="max-height: 200px; overflow-y: auto"
          >
            <VAlert
              v-for="(error, index) in importDepartmentErrors"
              :key="index"
              color="error"
              variant="tonal"
              density="compact"
            >
              {{ error.row ? `Row ${error.row}: ` : ""
              }}{{ error.message || error }}
            </VAlert>
          </div>
        </VCardText>

        <!-- Loading Bar -->
        <VCardText
          v-if="isImportDepartmentLoading"
          class="pt-0 pb-2"
        >
          <VProgressLinear
            indeterminate
            color="primary"
            height="6"
            rounded
            class="mb-1"
          />
        </VCardText>

        <!-- Action Buttons -->
        <VCardActions class="px-5 pb-5">
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="isImportDepartmentLoading"
            @click="downloadSampleDepartmentFile"
          >
            Download Sample
          </VBtn>
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="isImportDepartmentLoading"
            @click="isImportDepartmentModal = false"
          >
            Close
          </VBtn>
          <VBtn
            :loading="isImportDepartmentLoading"
            :disabled="isImportDepartmentLoading || !importDepartmentFile"
            @click="submitImportDepartment"
          >
            Import
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
